// pages/schedule/index.tsx
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import {
    Box, Tabs, Tab, Typography, Button,
    Grid, CircularProgress, Menu, MenuItem,
    Paper, Divider, IconButton, Tooltip,
    useTheme
} from '@mui/material';
import {
    Add as AddIcon,
    Today as TodayIcon,
    Print as PrintIcon,
    ArrowDropDown as ArrowDropDownIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    CalendarViewWeek as CalendarViewWeekIcon,
    CalendarViewMonth as CalendarViewMonthIcon,
    Construction as ConstructionIcon,
    MeetingRoom as MeetingRoomIcon
} from '@mui/icons-material';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, addWeeks, subWeeks, parseISO, addDays } from 'date-fns';
import { ja } from 'date-fns/locale';
import { combinedScheduleService } from '../../services/combinedSchedule';
import { workPreferenceService } from '../../services/workPreference';
import { Schedule } from '../../types/schedule';
import { OtherSchedule } from '../../types/otherSchedule';
import { CombinedScheduleItem } from '../../types/commonSchedule';
import ScheduleTomorrow from '../../components/schedule/ScheduleTomorrow';
import ScheduleWeekView from '../../components/schedule/ScheduleWeekView';
import ScheduleMonthView from '../../components/schedule/ScheduleMonthView';
import ScheduleDialog from '../../components/schedule/ScheduleDialog/index';
import StaffUtilizationDialog from '../../components/schedule/StaffUtilizationDialog';
import UncompletedConstructionDialog from '../../components/schedule/UncompletedConstructionDialog';
import { AvailableMember } from '../../types/workPreference';
import { useResponsive } from '../../hooks/useResponsive';
import ScheduleWeekListView from '../../components/schedule/ScheduleWeekListView';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import EngineeringIcon from '@mui/icons-material/Engineering';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import { alpha } from '@mui/material/styles';

const SchedulePage: React.FC = () => {
    const theme = useTheme();
    const { isDesktop, isTablet, isMobile } = useResponsive();

    // 基本状態
    const [viewMode, setViewMode] = useState<'week' | 'month'>('week');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [schedules, setSchedules] = useState<CombinedScheduleItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    // 表示設定
    const [displayMode, setDisplayMode] = useState<'grid' | 'list'>(isMobile ? 'list' : 'grid');
    const [selectedTeam, setSelectedTeam] = useState<'equipment' | 'scaffolding'>('equipment');
    const [useSpecialDisplay, setUseSpecialDisplay] = useState(true);

    // ダイアログ設定
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [selectedSchedule, setSelectedSchedule] = useState<CombinedScheduleItem | null>(null);
    const [selectedScheduleType, setSelectedScheduleType] = useState<'construction' | 'other'>('construction');
    const [staffDialogOpen, setStaffDialogOpen] = useState(false);
    const [constructionDialogOpen, setConstructionDialogOpen] = useState(false);

    // 新規予定追加メニュー
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(anchorEl);

    // モバイル時に自動的にリスト表示に切り替え
    useEffect(() => {
        if (isMobile) {
            setDisplayMode('list');
        }
    }, [isMobile]);

    // 表示形式切り替え処理
    const handleDisplayModeChange = useCallback((_: React.MouseEvent<HTMLElement>, newValue: 'grid' | 'list') => {
        if (newValue !== null) {
            setDisplayMode(newValue);
        }
    }, []);


    // 日付操作
    const handlePrev = useCallback(() => {
        if (viewMode === 'week') {
            if (useSpecialDisplay) {
                // 特殊表示モードの場合、通常表示モードに切り替える
                setUseSpecialDisplay(false);

                // 今日の曜日に基づいて、前週の月曜日を設定
                const today = new Date();
                const todayDayOfWeek = today.getDay();
                const daysUntilPrevMonday = todayDayOfWeek === 1 ? 7 : todayDayOfWeek === 0 ? 6 : todayDayOfWeek - 1;

                const prevMonday = new Date(today);
                prevMonday.setDate(today.getDate() - daysUntilPrevMonday - 7); // 前週の月曜日

                setCurrentDate(prevMonday);
            } else {
                // 既に通常表示モードなら単に1週間戻る
                setCurrentDate(prevDate => subWeeks(prevDate, 1));
            }
        } else {
            setCurrentDate(prevDate => subMonths(prevDate, 1));
        }
    }, [viewMode, useSpecialDisplay]);

    const handleNext = useCallback(() => {
        // 翌週ボタンクリック時は通常表示モードに切り替え
        if (viewMode === 'week') {
            if (useSpecialDisplay) {
                // 特殊表示モードの場合、通常表示モードに切り替える
                setUseSpecialDisplay(false);

                // 今日の曜日に基づいて、来週の月曜日を設定
                const today = new Date();
                const todayDayOfWeek = today.getDay();
                const daysUntilNextMonday = todayDayOfWeek === 0 ? 1 : 8 - todayDayOfWeek;

                const nextMonday = new Date(today);
                nextMonday.setDate(today.getDate() + daysUntilNextMonday);

                setCurrentDate(nextMonday);
            } else {
                // 既に通常表示モードなら単に1週間進める
                setCurrentDate(prevDate => addWeeks(prevDate, 1));
            }
        } else {
            setCurrentDate(prevDate => addMonths(prevDate, 1));
        }
    }, [viewMode, useSpecialDisplay]);

    // 今日ボタンで初期表示に戻る
    const handleToday = useCallback(() => {
        setCurrentDate(new Date());
        setUseSpecialDisplay(true); // 特殊表示モードに戻す
    }, []);

    // タブ切り替え
    const handleViewChange = useCallback((_: React.SyntheticEvent, newValue: 'week' | 'month') => {
        // モバイル表示時は週間表示のみ許可
        if (isMobile && newValue === 'month') {
            return;
        }
        setViewMode(newValue);
    }, [isMobile]);

    // チーム切り替え
    const handleTeamChange = useCallback((_: React.SyntheticEvent, newValue: 'equipment' | 'scaffolding') => {
        if (newValue) {
            setSelectedTeam(newValue);
        }
    }, []);

    // 日付クリック
    const handleDateClick = useCallback((date: Date, teamType: 'equipment' | 'scaffolding') => {
        // 新規スケジュール作成のために既存のhandleOpenDialogを利用
        handleOpenDialog(date, undefined, teamType);
    }, []);

    // 予定クリック
    const handleScheduleClick = useCallback((schedule: CombinedScheduleItem) => {
        handleOpenDialog(undefined, schedule);
    }, []);

    // 新規予定追加メニュー表示
    const handleAddMenuClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    }, []);

    // 新規予定追加メニュー選択
    const handleAddMenuItemClick = useCallback((type: 'construction' | 'other') => {
        setAnchorEl(null);
        setSelectedScheduleType(type);
        handleOpenDialog(new Date(), undefined, null, type);
    }, []);

    // 新規予定追加（チーム指定あり）
    const handleAddSchedule = useCallback((date: Date, teamType: 'equipment' | 'scaffolding', scheduleType?: 'construction' | 'other') => {
        setSelectedDate(date);
        setSelectedTeam(teamType);
        setSelectedScheduleType(scheduleType || 'construction');
        setDialogOpen(true);
    }, []);

    // 予定ダイアログ
    const handleOpenDialog = useCallback((date?: Date, schedule?: CombinedScheduleItem, teamType?: 'equipment' | 'scaffolding' | null, scheduleType?: 'construction' | 'other') => {
        if (schedule) {
            setSelectedSchedule(schedule);
            setSelectedDate(new Date(schedule.scheduleDate));
            setSelectedTeam(schedule.teamType as 'equipment' | 'scaffolding');
            setSelectedScheduleType(schedule.type);
        } else {
            setSelectedSchedule(null);
            setSelectedDate(date || new Date());
            if (teamType) setSelectedTeam(teamType);
            setSelectedScheduleType(scheduleType || 'construction');
        }
        setDialogOpen(true);
    }, []);

    const handleCloseDialog = useCallback(() => {
        setDialogOpen(false);
        setSelectedSchedule(null);
        setSelectedDate(null);
    }, []);

    // データ保存
    const handleSaveSchedule = useCallback(async (scheduleData: Schedule | OtherSchedule, type: 'construction' | 'other') => {
        setIsLoading(true);
        try {
            // チーム情報を追加
            if (!scheduleData.teamType) {
                scheduleData.teamType = selectedTeam;
            }

            if (selectedSchedule?.id) {
                // 更新
                await combinedScheduleService.updateSchedule(selectedSchedule.id, scheduleData, type);
            } else {
                // 新規作成
                await combinedScheduleService.createSchedule(scheduleData, type);
            }
            await fetchSchedules();
            handleCloseDialog();
        } catch (error) {
            console.error('Error saving schedule:', error);
        } finally {
            setIsLoading(false);
        }
    }, [selectedSchedule, selectedTeam, handleCloseDialog]);

    // データ削除
    const handleDeleteSchedule = useCallback(async (id: number, type: 'construction' | 'other') => {
        if (window.confirm('この予定を削除してもよろしいですか？')) {
            setIsLoading(true);
            try {
                await combinedScheduleService.deleteSchedule(id, type);
                await fetchSchedules();
                handleCloseDialog();
            } catch (error) {
                console.error('Error deleting schedule:', error);
            } finally {
                setIsLoading(false);
            }
        }
    }, [handleCloseDialog]);

    // データ取得
    const fetchSchedules = useCallback(async () => {
        setIsLoading(true);
        try {
          // カレンダー表示用の期間
          const start = format(startOfMonth(currentDate), 'yyyy-MM-dd');
          const end = format(endOfMonth(currentDate), 'yyyy-MM-dd');
          
          // 明日の日付
          const tomorrow = format(addDays(new Date(), 1), 'yyyy-MM-dd');
          
          // 表示期間に明日が含まれているかチェック
          const tomorrowIncluded = tomorrow >= start && tomorrow <= end;
          
          // 明日が含まれていない場合は、表示期間 + 明日の予定を取得
          if (!tomorrowIncluded) {
            // 通常の期間の予定を取得
            const data = await combinedScheduleService.getCombinedSchedules(start, end);
            
            // 明日の予定を別途取得
            const tomorrowData = await combinedScheduleService.getCombinedSchedules(tomorrow, tomorrow);
            
            // 両方の結果をマージ
            setSchedules([...(data || []), ...(tomorrowData || [])]);
          } else {
            // 明日が表示期間に含まれている場合は通常通り
            const data = await combinedScheduleService.getCombinedSchedules(start, end);
            setSchedules(data || []);
          }
        } catch (error) {
          console.error('Error fetching schedules:', error);
        } finally {
          setIsLoading(false);
        }
      }, [currentDate]);
      

    // 未割当メンバー取得
    // const fetchAvailableMembers = useCallback(async (date: string, departmentType: string): Promise<AvailableMember[]> => {
    //     try {
    //         // 日本語の部門タイプを英語に変換
    //         const englishDepartmentType =
    //             departmentType === '設備工事' ? 'equipment' as const :
    //                 departmentType === '仮設工事' ? 'scaffolding' as const :
    //                     departmentType as 'equipment' | 'scaffolding';

    //         // 文字列の日付をDate型に変換
    //         const dateObj = parseISO(date);

    //         // workPreferenceServiceのメソッドを呼び出し
    //         return await workPreferenceService.getAvailableMembers(dateObj, englishDepartmentType);
    //     } catch (error) {
    //         console.error('Error fetching available members:', error);
    //         return [];
    //     }
    // }, []);

    // 未割当メンバー取得
const fetchAvailableMembers = useCallback(async (date: string, departmentType: string): Promise<AvailableMember[]> => {
    try {
        // 日本語の部門タイプを英語に変換
        let englishDepartmentType: 'equipment' | 'scaffolding';
        
        if (departmentType === '設備工事') {
            englishDepartmentType = 'equipment';
        } else if (departmentType === '仮設工事') {
            englishDepartmentType = 'scaffolding';
        } else if (departmentType === 'equipment' || departmentType === 'scaffolding') {
            englishDepartmentType = departmentType as 'equipment' | 'scaffolding';
        } else {
            console.error(`Invalid department type: ${departmentType}`);
            return [];
        }
        
        // 文字列の日付をDate型に変換（UTCからローカル時間への変換を確実に行う）
        const dateObj = new Date(date);
        dateObj.setHours(0, 0, 0, 0); // 時間部分をリセット

        // workPreferenceServiceのメソッドを呼び出し
        const members = await workPreferenceService.getAvailableMembers(dateObj, englishDepartmentType);

        return members;
    } catch (error) {
        console.error(`Error in fetchAvailableMembers for ${date}, ${departmentType}:`, error);
        return [];
    }
}, []);

    // 日付変更時にデータ再取得
    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            if (isMounted) {
                setIsLoading(true);
                try {
                    await fetchSchedules();
                } finally {
                    if (isMounted) {
                        setIsLoading(false);
                    }
                }
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, [currentDate, fetchSchedules]);

    // 表示タイトル
    const periodTitle = (() => {
        if (viewMode === 'week') {
            // 現在の表示の基準日から週の開始日と終了日を正確に計算
            const startOfWeek = new Date(currentDate);
            const dayOfWeek = startOfWeek.getDay();

            // 月曜日を週の開始日として調整
            const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // 日曜日は-6、月曜日は0、火曜日は-1...
            startOfWeek.setDate(startOfWeek.getDate() + diff);

            // 週の終了日は開始日から6日後（日曜日）
            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6);

            return `${format(startOfWeek, 'yyyy年M月d日', { locale: ja })} 〜 ${format(endOfWeek, 'M月d日', { locale: ja })}`;
        } else {
            // 月表示の場合は年月を表示（変更なし）
            return format(currentDate, 'yyyy年M月', { locale: ja });
        }
    })();

    // 週を変更する関数
    const handleWeekChange = (direction: 'prev' | 'next') => {
        if (direction === 'prev') {
            handlePrev();
        } else {
            handleNext();
        }
    };



    return (
        <Box sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            p: { xs: 0.5, sm: 1 }
        }}>
            <Paper elevation={2} sx={{
                width: '100%',
                bgcolor: 'background.paper',
                borderRadius: { xs: 1, sm: 2 },
                overflow: 'hidden',
                mb: 2
            }}>
                {/* ヘッダー部分 */}
                <Box sx={{
                    p: { xs: 1, sm: 2 },
                    borderBottom: `1px solid ${theme.palette.divider}`
                }}>
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: { xs: 2, sm: 0 }
                    }}>
                        {/* 左側：タイトルと期間操作 */}
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'center', sm: 'center' },
                            gap: { xs: 1, sm: 2 }
                        }}>
                            {/* タイトル（モバイルでは非表示） */}
                            <Typography
                                variant="h6"
                                component="h1"
                                sx={{
                                    display: { xs: 'none', sm: 'block' },
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                スケジュール管理
                            </Typography>

                            {/* 期間操作 */}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <IconButton
                                    size="small"
                                    onClick={handlePrev}
                                    sx={{ color: theme.palette.primary.main }}
                                >
                                    <ChevronLeftIcon />
                                </IconButton>

                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 'medium',
                                        mx: { xs: 1, sm: 2 },
                                        minWidth: { xs: 'auto', sm: '200px' },
                                        textAlign: 'center'
                                    }}
                                >
                                    {periodTitle}
                                </Typography>

                                <IconButton
                                    size="small"
                                    onClick={handleNext}
                                    sx={{ color: theme.palette.primary.main }}
                                >
                                    <ChevronRightIcon />
                                </IconButton>

                                <Tooltip title="今日">
                                    <IconButton
                                        size="small"
                                        onClick={handleToday}
                                        sx={{
                                            ml: 1,
                                            color: theme.palette.secondary.main,
                                            bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                            '&:hover': {
                                                bgcolor: alpha(theme.palette.secondary.main, 0.2)
                                            }
                                        }}
                                    >
                                        <TodayIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        {/* 右側：表示切替 */}
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'stretch', sm: 'center' },
                            gap: { xs: 1, sm: 2 }
                        }}>
                            {/* 週間/月間 切替 - モバイルでは週間のみ表示 */}
                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={handleViewChange}
                                size="small"
                                sx={{
                                    display: 'flex',
                                    '& .MuiToggleButtonGroup-grouped': {
                                        flex: 1
                                    }
                                }}
                            >
                                <ToggleButton value="week" aria-label="週間表示">
                                    <CalendarViewWeekIcon fontSize="small" />
                                    <Box component="span" sx={{
                                        ml: 0.5,
                                        display: { xs: 'none', sm: 'inline' }
                                    }}>
                                        週間
                                    </Box>
                                </ToggleButton>
                                {/* モバイル表示時は月間表示ボタンを非表示 */}
                                {!isMobile && (
                                    <ToggleButton value="month" aria-label="月間表示">
                                        <CalendarViewMonthIcon fontSize="small" />
                                        <Box component="span" sx={{
                                            ml: 0.5,
                                            display: { xs: 'none', sm: 'inline' }
                                        }}>
                                            月間
                                        </Box>
                                    </ToggleButton>
                                )}
                            </ToggleButtonGroup>

                            {/* 表示形式切り替え */}
                            {viewMode === 'week' && (
                                <ToggleButtonGroup
                                    value={displayMode}
                                    exclusive
                                    onChange={handleDisplayModeChange}
                                    size="small"
                                    sx={{
                                        display: { xs: 'none', sm: 'flex' } // モバイルでは非表示
                                    }}
                                >
                                    <ToggleButton value="grid" disabled={isMobile}>
                                        <GridViewIcon fontSize="small" />
                                    </ToggleButton>
                                    <ToggleButton value="list">
                                        <ViewListIcon fontSize="small" />
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            )}

                            {/* 設備/仮設 切替（モバイル/タブレット表示時のみ） */}
                            {(isMobile || isTablet) && viewMode === 'week' && (
                                <Tabs
                                    value={selectedTeam}
                                    onChange={handleTeamChange}
                                    variant="fullWidth"
                                    sx={{
                                        minHeight: 40,
                                        '& .MuiTab-root': {
                                            minHeight: 40,
                                            py: 0.5
                                        }
                                    }}
                                >
                                    <Tab
                                        value="equipment"
                                        label={
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5
                                            }}>
                                                <SettingsInputComponentIcon fontSize="small" />
                                                <span>設備班</span>
                                            </Box>
                                        }
                                    />
                                    <Tab
                                        value="scaffolding"
                                        label={
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5
                                            }}>
                                                <EngineeringIcon fontSize="small" />
                                                <span>仮設班</span>
                                            </Box>
                                        }
                                    />
                                </Tabs>
                            )}
                        </Box>
                    </Box>
                </Box>

                {/* メインコンテンツ */}
                <Box sx={{ p: { xs: 0.5, sm: 1, md: 2 } }}>
                    {isLoading ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '50vh'
                        }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            gap: { xs: 2, md: 2 }
                        }}>
                            {/* 週間/月間ビュー */}
                            <Box sx={{
                                flex: 1,
                                width: '100%'
                            }}>
                                {viewMode === 'week' ? (
                                    <>
                                        {/* デスクトップまたはタブレットのグリッド表示 */}
                                        {(displayMode === 'grid' && !isMobile) ? (
                                            <ScheduleWeekView
                                                schedules={schedules}
                                                currentDate={currentDate}
                                                onDateClick={handleDateClick}
                                                onScheduleClick={handleScheduleClick}
                                                onAddSchedule={handleAddSchedule}
                                                useSpecialDisplay={useSpecialDisplay}
                                                fetchAvailableMembers={fetchAvailableMembers}
                                                onWeekChange={handleWeekChange}
                                            />
                                        ) : (
                                            // 新しいScheduleWeekListViewコンポーネントを使用
                                            <ScheduleWeekListView
                                                schedules={schedules}
                                                currentDate={currentDate}
                                                showBothTeams={!isMobile && !isTablet} // モバイル/タブレットでは片方だけ表示
                                                selectedTeam={selectedTeam} // モバイル/タブレット表示時に選択中の班
                                                onDateClick={handleDateClick}
                                                onScheduleClick={handleScheduleClick}
                                                onAddSchedule={handleAddSchedule}
                                                fetchAvailableMembers={fetchAvailableMembers}
                                                onWeekChange={handleWeekChange}
                                            />
                                        )}
                                    </>
                                ) : (
                                    /* 月間表示 */
                                    <ScheduleMonthView
                                        schedules={schedules}
                                        currentDate={currentDate}
                                        onDateClick={handleDateClick}
                                        onScheduleClick={handleScheduleClick}
                                        onMonthChange={setCurrentDate}
                                    />
                                )}
                            </Box>

                            {/* 明日の予定 (デスクトップのみ表示) */}
                            {viewMode === 'week' && isDesktop && (
                                <Box sx={{
                                    width: { md: '300px' },
                                    display: { xs: 'none', md: 'block' }
                                }}>
                                    <ScheduleTomorrow
                                        schedules={schedules}
                                        currentDate={currentDate}
                                        onScheduleClick={handleScheduleClick}
                                    />
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* 固定の新規予定追加ボタン (モバイル/タブレット表示) */}
            {(isMobile || isTablet) && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 16,
                        right: 16,
                        zIndex: 1000
                    }}
                >
                    <Tooltip title="予定追加">
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={(e) => handleAddMenuClick(e)}
                            sx={{
                                borderRadius: '50%',
                                minWidth: 'auto',
                                width: 56,
                                height: 56,
                                boxShadow: theme.shadows[4]
                            }}
                        >
                            <AddIcon />
                        </Button>
                    </Tooltip>
                </Box>
            )}

            {/* 新規予定追加メニュー */}
            <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={() => setAnchorEl(null)}
            >
                <MenuItem
                    onClick={() => handleAddMenuItemClick('construction')}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <ConstructionIcon fontSize="small" color="primary" />
                    工事予定を追加
                </MenuItem>
                <MenuItem
                    onClick={() => handleAddMenuItemClick('other')}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <MeetingRoomIcon fontSize="small" color="secondary" />
                    その他予定を追加
                </MenuItem>
            </Menu>

            {/* 各種ダイアログ */}
            <ScheduleDialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                onSave={handleSaveSchedule}
                onDelete={selectedSchedule?.id ? handleDeleteSchedule : undefined}
                initialData={selectedSchedule}
                selectedDate={selectedDate}
                initialTeam={selectedTeam}
                isLoading={isLoading}
                initialScheduleType={selectedScheduleType}
            />

            <StaffUtilizationDialog
                open={staffDialogOpen}
                onClose={() => setStaffDialogOpen(false)}
                month={currentDate}
                schedules={schedules as any}
            />

            <UncompletedConstructionDialog
                open={constructionDialogOpen}
                onClose={() => setConstructionDialogOpen(false)}
                onSelectConstruction={(constructionId, date) => {
                    setConstructionDialogOpen(false);
                    // 未完了工事からは必ず工事予定として登録
                    setSelectedScheduleType('construction');
                    handleOpenDialog(date, undefined, null, 'construction');
                    // ダイアログ内のデータ設定のためにconstructionIdを渡す必要があるが、
                    // CombinedScheduleItemの形式に合わせる必要がある
                    setSelectedSchedule({
                        id: 0, // 新規作成用の仮ID
                        type: 'construction',
                        constructionId,
                        scheduleDate: format(date, 'yyyy-MM-dd'),
                        isAllDay: false,
                        memberData: [],
                        vehicleData: []
                    });
                }}
            />
        </Box>
    );
};

export default SchedulePage;